import { useEffect, useRef } from 'react';
import L from 'leaflet';
import {
  getAreaTypeColor, EXCLUSION_TYPE_COLORS, isExclusionArea,
} from '../../SiteTakeoff/siteTakeoffConstants';

/**
 * Syncs an array of areas to Leaflet layers on the map.
 * Adds/updates/removes layers to match the current `areas` state.
 * Calls onAreaClick(areaId) when the user clicks a layer.
 *
 * Parent polygons with exclusions are rendered as donuts (outer ring + hole rings).
 * Exclusion areas are rendered with dashed borders and gray fill.
 */
export default function useAreaRendering({ mapRef, drawnItemsRef, areas, onAreaClick, visible = true, activeAreaId = null, draggableAreaId = null, onAreaMoved = null, hiddenAreaIds = new Set(), showMeasurements = false, editingShapeAreaId = null, layerMapRef: externalLayerMapRef = null }) {
  const internalLayerMapRef = useRef({}); // areaId -> leaflet layer
  const layerMapRef = externalLayerMapRef || internalLayerMapRef;
  const labelLayerRef = useRef(null); // L.LayerGroup for measurement labels
  const prevActiveRef = useRef(null); // track previous active for de-highlight
  const dragStateRef = useRef(null); // { areaId, startLatLng }
  const onAreaClickRef = useRef(onAreaClick);

  useEffect(() => {
    onAreaClickRef.current = onAreaClick;
  }, [onAreaClick]);

  // Highlight / de-highlight the active area
  useEffect(() => {
    const prevId = prevActiveRef.current;
    const map = mapRef.current;
    if (!map) return;

    // De-highlight previous
    if (prevId && prevId !== activeAreaId && layerMapRef.current[prevId]) {
      const layer = layerMapRef.current[prevId];
      const area = areas.find(a => (a._id || a.id) === prevId);
      if (area && layer.setStyle) {
        const isExc = isExclusionArea(area);
        const baseColor = isExc
          ? ((area.exclusionType && EXCLUSION_TYPE_COLORS[area.exclusionType]) || '#9E9E9E')
          : getAreaTypeColor(area.areaType);
        layer.setStyle({
          weight: 2,
          color: isExc ? '#333' : (area.strokeColor || '#000'),
          fillOpacity: isExc ? 0.45 : 0.3,
          ...(isExc ? {} : { fillColor: baseColor }),
        });
      }
      if (area && layer instanceof L.Marker) {
        const baseColor = getAreaTypeColor(area.areaType);
        layer.setIcon(L.divIcon({
          className: '',
          html: `<div style="width:18px;height:18px;border-radius:50%;background:${baseColor};border:2px solid #fff;box-shadow:0 1px 3px rgba(0,0,0,0.4);"></div>`,
          iconSize: [18, 18],
          iconAnchor: [9, 9],
        }));
      }
    }

    // Highlight current
    if (activeAreaId && layerMapRef.current[activeAreaId]) {
      const layer = layerMapRef.current[activeAreaId];
      const area = areas.find(a => (a._id || a.id) === activeAreaId);
      if (area && layer.setStyle) {
        layer.setStyle({
          weight: 4,
          color: '#1976d2',
          fillOpacity: 0.45,
        });
      }
      if (area && layer instanceof L.Marker) {
        const baseColor = getAreaTypeColor(area.areaType);
        layer.setIcon(L.divIcon({
          className: '',
          html: `<div style="width:22px;height:22px;border-radius:50%;background:${baseColor};border:3px solid #1976d2;box-shadow:0 0 8px rgba(25,118,210,0.6);"></div>`,
          iconSize: [22, 22],
          iconAnchor: [11, 11],
        }));
      }
    }

    prevActiveRef.current = activeAreaId;
  }, [activeAreaId, areas, mapRef]);

  useEffect(() => {
    const map = mapRef.current;
    const drawnItems = drawnItemsRef.current;
    if (!map || !drawnItems) return;

    const currentIds = new Set(areas.map(a => a._id || a.id));

    // Build a map of parentAreaId -> child exclusion areas for donut rendering
    const exclusionsByParent = {};
    areas.forEach(area => {
      if (isExclusionArea(area) && area.paths?.length) {
        const parentId = typeof area.parentAreaId === 'object' ? area.parentAreaId.toString() : area.parentAreaId;
        if (!exclusionsByParent[parentId]) exclusionsByParent[parentId] = [];
        exclusionsByParent[parentId].push(area);
      }
    });

    // Remove layers that no longer exist
    Object.keys(layerMapRef.current).forEach(id => {
      if (!currentIds.has(id)) {
        const layer = layerMapRef.current[id];
        try { drawnItems.removeLayer(layer); } catch (_) {}
        delete layerMapRef.current[id];
      }
    });

    // Track which parent IDs need re-rendering due to exclusion changes
    // We re-render parents when their exclusion count/paths change
    const parentIdsWithExclusions = new Set(Object.keys(exclusionsByParent));

    // Add/update layers for current areas
    areas.forEach(area => {
      const areaId = area._id || area.id;
      if (!area.paths?.length) return;

      // Skip hidden areas — remove layer if it exists
      if (hiddenAreaIds.has(areaId)) {
        if (layerMapRef.current[areaId]) {
          try { drawnItems.removeLayer(layerMapRef.current[areaId]); } catch (_) {}
          delete layerMapRef.current[areaId];
        }
        return;
      }

      const isExclusion = isExclusionArea(area);
      const hasExclusions = !isExclusion && parentIdsWithExclusions.has(areaId);

      // For parents with exclusions, re-render to pick up hole changes —
      // but NOT while the layer is being actively edited (would destroy editing handles)
      if (hasExclusions && layerMapRef.current[areaId] && areaId !== editingShapeAreaId) {
        try { drawnItems.removeLayer(layerMapRef.current[areaId]); } catch (_) {}
        delete layerMapRef.current[areaId];
      }

      // Already rendered — skip
      if (layerMapRef.current[areaId]) return;

      const color = getAreaTypeColor(area.areaType);
      let layer;

      if (area.geometryType === 'point') {
        const p = area.paths[0];
        if (!p?.lat || !p?.lng) return;
        layer = L.marker([p.lat, p.lng], {
          draggable: true,   // Disabled at the handler level by default — enabled via useAreaEditing
          autoPan: true,
          icon: L.divIcon({
            className: '',
            html: `<div style="width:18px;height:18px;border-radius:50%;background:${color};border:2px solid #fff;box-shadow:0 1px 3px rgba(0,0,0,0.4);"></div>`,
            iconSize: [18, 18],
            iconAnchor: [9, 9],
          }),
        });
        // Disable drag initially — only useAreaEditing should enable it
        if (layer.dragging) {
          try { layer.dragging.disable(); } catch (_) {}
        }
      } else if (area.geometryType === 'ellipse') {
        const p = area.paths[0];
        if (!p?.lat || !p?.lng) return;
        const radiusMeters = (area.radiusX || 0) / 3.28084;
        layer = L.circle([p.lat, p.lng], {
          radius: radiusMeters,
          fillColor: color,
          fillOpacity: 0.25,
          color,
          weight: 2,
        });
      } else if (area.geometryType === 'polyline') {
        const latlngs = area.paths.map(p => [p.lat, p.lng]);
        layer = L.polyline(latlngs, { color, weight: 4, opacity: 0.8 });
      } else if (isExclusion) {
        // Exclusion area — dashed border, gray fill, higher opacity
        const latlngs = area.paths.map(p => [p.lat, p.lng]);
        const excColor = (area.exclusionType && EXCLUSION_TYPE_COLORS[area.exclusionType]) || '#9E9E9E';
        layer = L.polygon(latlngs, {
          fillColor: excColor,
          fillOpacity: 0.45,
          color: '#333',
          weight: 2,
          dashArray: '6, 4',
        });
      } else {
        // polygon (default) — render with holes if exclusions exist
        const outerRing = area.paths.map(p => [p.lat, p.lng]);

        if (hasExclusions) {
          const holeRings = exclusionsByParent[areaId].map(exc =>
            exc.paths.map(p => [p.lat, p.lng])
          );
          layer = L.polygon([outerRing, ...holeRings], {
            fillColor: color,
            fillOpacity: 0.3,
            color: area.strokeColor || '#000',
            weight: 2,
          });
        } else {
          layer = L.polygon(outerRing, {
            fillColor: color,
            fillOpacity: 0.3,
            color: area.strokeColor || '#000',
            weight: 2,
          });
        }
      }

      if (!layer) return;

      layer.on('click', () => {
        if (onAreaClickRef.current) onAreaClickRef.current(areaId);
      });

      drawnItems.addLayer(layer);
      layerMapRef.current[areaId] = layer;
    });
  }, [areas, mapRef, drawnItemsRef, hiddenAreaIds]);

  // Show/hide the entire drawn-items layer group
  useEffect(() => {
    const map = mapRef.current;
    const drawnItems = drawnItemsRef.current;
    if (!map || !drawnItems) return;
    if (visible) {
      if (!map.hasLayer(drawnItems)) map.addLayer(drawnItems);
    } else {
      if (map.hasLayer(drawnItems)) map.removeLayer(drawnItems);
    }
  }, [visible, mapRef, drawnItemsRef]);

  // Make a polygon/polyline/circle draggable when draggableAreaId is set
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !draggableAreaId || !onAreaMoved) return;
    const layer = layerMapRef.current[draggableAreaId];
    if (!layer) return;

    // For markers, enable built-in dragging
    if (layer instanceof L.Marker) {
      if (layer.dragging) layer.dragging.enable();
      const onDragEnd = () => {
        const ll = layer.getLatLng();
        onAreaMoved(draggableAreaId, [{ lat: ll.lat, lng: ll.lng }]);
      };
      layer.on('dragend', onDragEnd);
      return () => {
        if (layer.dragging) layer.dragging.disable();
        layer.off('dragend', onDragEnd);
      };
    }

    // For polygons/polylines/circles — manual drag via raw DOM events so the
    // map pan handler cannot grab the mousedown before the shape does.
    let startLatLng = null;
    let movedDuringDrag = false;
    let mapDraggingDisabledByDrag = false;

    // Apply grab cursor to the layer element and ensure it's interactive
    if (layer.setStyle) {
      layer.setStyle({ interactive: true, cursor: 'grab' });
    }
    const el = layer.getElement?.();
    if (el) {
      el.style.cursor = 'grab';
      el.style.pointerEvents = 'auto';
    }

    const getEventLatLng = (event) => {
      if (event?.latlng) return event.latlng;
      const originalEvent = event?.originalEvent || event;
      if (!originalEvent) return null;
      try {
        return map.mouseEventToLatLng(originalEvent);
      } catch (_) {
        return null;
      }
    };

    const restoreMapDragging = () => {
      if (!mapDraggingDisabledByDrag) return;
      try { map.dragging.enable(); } catch (_) {}
      mapDraggingDisabledByDrag = false;
    };

    const onMouseDown = (e) => {
      // Stop event from reaching map handlers or other layers
      const originalEvent = e.originalEvent || e;
      if (originalEvent && L.DomEvent) {
        L.DomEvent.stop(originalEvent);
      }

      startLatLng = getEventLatLng(e);
      if (!startLatLng) return;
      movedDuringDrag = false;

      // Disable map dragging after we have captured the drag start.
      if (map.dragging?.enabled?.()) {
        map.dragging.disable();
        mapDraggingDisabledByDrag = true;
      }

      if (el) el.style.cursor = 'grabbing';

      L.DomEvent.on(document, 'mousemove', onMouseMove);
      L.DomEvent.on(document, 'mouseup', onMouseUp);
    };

    const onMouseMove = (e) => {
      const originalEvent = e.originalEvent || e;
      if (originalEvent && L.DomEvent) {
        L.DomEvent.stop(originalEvent);
      }

      const nextLatLng = getEventLatLng(e);
      if (!startLatLng || !nextLatLng) return;
      const dLat = nextLatLng.lat - startLatLng.lat;
      const dLng = nextLatLng.lng - startLatLng.lng;
      startLatLng = nextLatLng;
      movedDuringDrag = movedDuringDrag || Math.abs(dLat) > 0.0000001 || Math.abs(dLng) > 0.0000001;

      if (layer instanceof L.Circle) {
        const c = layer.getLatLng();
        layer.setLatLng([c.lat + dLat, c.lng + dLng]);
      } else if (layer.getLatLngs) {
        const shift = (latlngs) => {
          if (Array.isArray(latlngs[0]) && !('lat' in latlngs[0])) {
            return latlngs.map(ring => shift(ring));
          }
          return latlngs.map(ll => L.latLng(ll.lat + dLat, ll.lng + dLng));
        };
        layer.setLatLngs(shift(layer.getLatLngs()));
      }
    };

    const onMouseUp = (e) => {
      L.DomEvent.off(document, 'mousemove', onMouseMove);
      L.DomEvent.off(document, 'mouseup', onMouseUp);
      restoreMapDragging();
      if (el) el.style.cursor = 'grab';

      // Stop mouseup from triggering a map click as well
      const originalEvent = e?.originalEvent || e;
      if (originalEvent && L.DomEvent) {
        L.DomEvent.stop(originalEvent);
      }

      if (!movedDuringDrag) {
        if (onAreaClickRef.current) onAreaClickRef.current(draggableAreaId);
        startLatLng = null;
        return;
      }

      // Extract final paths
      let newPaths;
      if (layer instanceof L.Circle) {
        const c = layer.getLatLng();
        newPaths = [{ lat: c.lat, lng: c.lng }];
      } else if (layer.getLatLngs) {
        const lls = layer.getLatLngs();
        // Handle both simple polygons and polygons with holes
        const getPaths = (latlngs) => {
          if (Array.isArray(latlngs[0]) && !('lat' in latlngs[0])) {
            return getPaths(latlngs[0]); // return the outer ring
          }
          return latlngs.map(ll => ({ lat: ll.lat, lng: ll.lng }));
        };
        newPaths = getPaths(lls);
      }
      if (newPaths) onAreaMoved(draggableAreaId, newPaths);
      startLatLng = null;
    };

    if (el) {
      L.DomEvent.on(el, 'mousedown', onMouseDown);
    } else {
      layer.on('mousedown', onMouseDown);
    }

    return () => {
      if (el) {
        L.DomEvent.off(el, 'mousedown', onMouseDown);
      } else {
        layer.off('mousedown', onMouseDown);
      }
      L.DomEvent.off(document, 'mousemove', onMouseMove);
      L.DomEvent.off(document, 'mouseup', onMouseUp);
      // Ensure we don't leave map dragging disabled if effect cleans up during drag
      restoreMapDragging();
      if (el) el.style.cursor = '';
    };
  }, [draggableAreaId, onAreaMoved, mapRef, areas]); // Added areas to re-sync if layer is recreated

  // Measurement labels — show per-segment lengths on polygon edges and polyline segments
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    if (labelLayerRef.current) {
      try { map.removeLayer(labelLayerRef.current); } catch (_) {}
      labelLayerRef.current = null;
    }

    if (!showMeasurements || !visible) return;

    const labelGroup = L.layerGroup();

    // Haversine distance between two points in feet
    const segmentFeet = (p1, p2) => {
      const a = L.latLng(p1.lat, p1.lng);
      const b = L.latLng(p2.lat, p2.lng);
      return a.distanceTo(b) * 3.28084;
    };

    const formatFt = (ft) => {
      if (ft >= 100) return `${Math.round(ft)}'`;
      return `${ft.toFixed(1)}'`;
    };

    const addSegmentLabel = (p1, p2) => {
      const ft = segmentFeet(p1, p2);
      if (ft < 1) return; // skip tiny segments
      const midLat = (p1.lat + p2.lat) / 2;
      const midLng = (p1.lng + p2.lng) / 2;

      const label = L.marker([midLat, midLng], {
        icon: L.divIcon({
          className: '',
          html: `<div style="
            background: rgba(0,0,0,0.75);
            color: #fff;
            font-size: 10px;
            font-weight: 600;
            padding: 1px 4px;
            border-radius: 2px;
            white-space: nowrap;
            pointer-events: none;
            transform: translate(-50%, -50%);
          ">${formatFt(ft)}</div>`,
          iconSize: [0, 0],
          iconAnchor: [0, 0],
        }),
        interactive: false,
      });
      labelGroup.addLayer(label);
    };

    areas.forEach(area => {
      const areaId = area._id || area.id;
      if (!area.paths?.length || hiddenAreaIds.has(areaId)) return;
      if (isExclusionArea(area)) return;
      if (area.geometryType === 'point' || area.geometryType === 'ellipse') return;

      const pts = area.paths;
      // Label each segment
      for (let i = 1; i < pts.length; i++) {
        addSegmentLabel(pts[i - 1], pts[i]);
      }
      // Close the polygon ring
      if (area.geometryType !== 'polyline' && pts.length >= 3) {
        addSegmentLabel(pts[pts.length - 1], pts[0]);
      }
    });

    labelGroup.addTo(map);
    labelLayerRef.current = labelGroup;

    return () => {
      if (labelLayerRef.current) {
        try { map.removeLayer(labelLayerRef.current); } catch (_) {}
        labelLayerRef.current = null;
      }
    };
  }, [areas, showMeasurements, visible, hiddenAreaIds, mapRef]);

  return { layerMapRef };
}
